"use client"

import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import SaveIcon from "@mui/icons-material/Save"
import WorkHistoryIcon from "@mui/icons-material/WorkHistory"
import AddCardIcon from "@mui/icons-material/AddCard"
import { ThemeProvider } from "@mui/material/styles"
import {
  RRHH_ESTADOS_LABORALES,
  RRHH_TIPOS_JORNADA,
  extractErrorMessage,
  formatCurrency,
  formatFecha,
  getEmpleadoNombreCompleto,
  rrhhTheme,
} from "./rrhhUi.js"

const buildEmptyEmpleadoForm = () => ({
  entidad: {
    descripcion: "",
    apellido: "",
    telefono: "",
    email: "",
    dniCuitCuil: "",
    direccion: "",
    localidad: "",
    provincia: "",
  },
  empleado: {
    legajo: "",
    fechaIngreso: "",
    fechaEgreso: "",
    estadoLaboral: "ACTIVO",
    idSupervisorEntidad: null,
    idNegocio: null,
    idUbicacionBase: null,
    cbu: "",
    observaciones: "",
  },
  contrato: {
    fechaVigenciaDesde: "",
    fechaVigenciaHasta: "",
    puesto: "",
    tipoJornada: "",
    sueldoBase: "",
    pagoPorHora: "",
    pagoPorHoraExtra: "",
    sindicatoGremio: "",
    categoria: "",
    observaciones: "",
  },
})

const mapEmpleadoDetailToForm = (empleadoDetail) => {
  if (!empleadoDetail?.empleado) return buildEmptyEmpleadoForm()

  const { empleado, contratoVigente } = empleadoDetail
  const entidad = empleado.entidad || {}

  return {
    entidad: {
      descripcion: entidad.descripcion || "",
      apellido: entidad.apellido || "",
      telefono: entidad.telefono || "",
      email: entidad.email || "",
      dniCuitCuil: entidad.dniCuitCuil || "",
      direccion: entidad.direccion || "",
      localidad: entidad.localidad || "",
      provincia: entidad.provincia || "",
    },
    empleado: {
      legajo: empleado.legajo || "",
      fechaIngreso: empleado.fechaIngreso || "",
      fechaEgreso: empleado.fechaEgreso || "",
      estadoLaboral: empleado.estadoLaboral || "ACTIVO",
      idSupervisorEntidad: empleado.idSupervisorEntidad || null,
      idNegocio: empleado.idNegocio || null,
      idUbicacionBase: empleado.idUbicacionBase || null,
      cbu: empleado.cbu || "",
      observaciones: empleado.observaciones || "",
    },
    contrato: {
      fechaVigenciaDesde: contratoVigente?.fechaVigenciaDesde || "",
      fechaVigenciaHasta: contratoVigente?.fechaVigenciaHasta || "",
      puesto: contratoVigente?.puesto || "",
      tipoJornada: contratoVigente?.tipoJornada || "",
      sueldoBase: contratoVigente?.sueldoBase ?? "",
      pagoPorHora: contratoVigente?.pagoPorHora ?? "",
      pagoPorHoraExtra: contratoVigente?.pagoPorHoraExtra ?? "",
      sindicatoGremio: contratoVigente?.sindicatoGremio || "",
      categoria: contratoVigente?.categoria || "",
      observaciones: contratoVigente?.observaciones || "",
    },
  }
}

const ModalRrhhEmpleado = ({
  open,
  mode = "create",
  empleadoDetail,
  loading = false,
  supervisorOptions = [],
  negocios = [],
  ubicaciones = [],
  onClose,
  onSave,
  onOpenContrato,
}) => {
  const [formData, setFormData] = useState(buildEmptyEmpleadoForm())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) {
      setError(null)
      setSaving(false)
      setFormData(mode === "edit" ? mapEmpleadoDetailToForm(empleadoDetail) : buildEmptyEmpleadoForm())
    }
  }, [open, mode, empleadoDetail])

  const supervisorFiltrado = useMemo(() => {
    const currentId = empleadoDetail?.empleado?.idEntidad
    return supervisorOptions.filter((option) => option.idEntidad !== currentId)
  }, [supervisorOptions, empleadoDetail])

  const selectedSupervisor = supervisorFiltrado.find(
    (option) => option.idEntidad === formData.empleado.idSupervisorEntidad
  ) || null

  const selectedNegocio = negocios.find((item) => item.id === formData.empleado.idNegocio) || null
  const selectedUbicacion = ubicaciones.find((item) => item.id === formData.empleado.idUbicacionBase) || null
  const contratoVigente = empleadoDetail?.contratoVigente || null

  const handleEntidadChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      entidad: {
        ...prev.entidad,
        [field]: event.target.value,
      },
    }))
  }

  const handleEmpleadoChange = (field) => (event) => {
    const value = event.target.value
    setFormData((prev) => ({
      ...prev,
      empleado: {
        ...prev.empleado,
        [field]: value,
      },
      contrato:
        field === "fechaIngreso" && !prev.contrato.fechaVigenciaDesde
          ? { ...prev.contrato, fechaVigenciaDesde: value }
          : prev.contrato,
    }))
  }

  const handleContratoChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      contrato: {
        ...prev.contrato,
        [field]: event.target.value,
      },
    }))
  }

  const buildPayload = () => {
    const entidad = { ...formData.entidad }
    const empleado = {
      ...formData.empleado,
      idSupervisorEntidad: formData.empleado.idSupervisorEntidad || null,
      idNegocio: formData.empleado.idNegocio || null,
      idUbicacionBase: formData.empleado.idUbicacionBase || null,
    }

    const payload = { entidad, empleado }

    if (mode === "create") {
      payload.contrato = {
        ...formData.contrato,
        fechaVigenciaDesde: formData.contrato.fechaVigenciaDesde || formData.empleado.fechaIngreso || null,
      }
    }

    return payload
  }

  const handleSubmit = async () => {
    if (!formData.entidad.descripcion.trim()) {
      setError("El nombre del empleado es requerido.")
      return
    }

    if (!formData.empleado.fechaIngreso) {
      setError("La fecha de ingreso es requerida.")
      return
    }

    try {
      setSaving(true)
      setError(null)
      await onSave(buildPayload())
    } catch (submitError) {
      setError(extractErrorMessage(submitError, "No se pudo guardar el empleado."))
    } finally {
      setSaving(false)
    }
  }

  return (
    <ThemeProvider theme={rrhhTheme}>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {mode === "edit" ? "Editar empleado" : "Nuevo empleado"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Base laboral RRHH conectada al dominio backend.
              </Typography>
            </Box>

            {mode === "edit" && empleadoDetail?.empleado?.legajo ? (
              <Chip label={`Legajo ${empleadoDetail.empleado.legajo}`} color="secondary" variant="outlined" />
            ) : null}
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 3 }}>
          {loading ? (
            <Typography variant="body2" color="text.secondary">
              Cargando datos del empleado...
            </Typography>
          ) : (
            <Stack spacing={3}>
              {error ? <Alert severity="error">{error}</Alert> : null}

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Datos personales
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Nombre"
                      value={formData.entidad.descripcion}
                      onChange={handleEntidadChange("descripcion")}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Apellido"
                      value={formData.entidad.apellido}
                      onChange={handleEntidadChange("apellido")}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="DNI / CUIL / CUIT"
                      value={formData.entidad.dniCuitCuil}
                      onChange={handleEntidadChange("dniCuitCuil")}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Teléfono"
                      value={formData.entidad.telefono}
                      onChange={handleEntidadChange("telefono")}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Email"
                      type="email"
                      value={formData.entidad.email}
                      onChange={handleEntidadChange("email")}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      label="Dirección"
                      value={formData.entidad.direccion}
                      onChange={handleEntidadChange("direccion")}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Localidad"
                      value={formData.entidad.localidad}
                      onChange={handleEntidadChange("localidad")}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Provincia"
                      value={formData.entidad.provincia}
                      onChange={handleEntidadChange("provincia")}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Ficha laboral
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Legajo"
                      value={formData.empleado.legajo}
                      onChange={handleEmpleadoChange("legajo")}
                      fullWidth
                      helperText="Si queda vacío, el backend lo genera automáticamente."
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Fecha ingreso"
                      type="date"
                      value={formData.empleado.fechaIngreso}
                      onChange={handleEmpleadoChange("fechaIngreso")}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Fecha egreso"
                      type="date"
                      value={formData.empleado.fechaEgreso}
                      onChange={handleEmpleadoChange("fechaEgreso")}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Autocomplete
                      options={RRHH_ESTADOS_LABORALES}
                      value={RRHH_ESTADOS_LABORALES.find((option) => option.value === formData.empleado.estadoLaboral) || null}
                      onChange={(_, option) => {
                        setFormData((prev) => ({
                          ...prev,
                          empleado: {
                            ...prev.empleado,
                            estadoLaboral: option?.value || "ACTIVO",
                          },
                        }))
                      }}
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => <TextField {...params} label="Estado laboral" fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={supervisorFiltrado}
                      value={selectedSupervisor}
                      onChange={(_, option) => {
                        setFormData((prev) => ({
                          ...prev,
                          empleado: {
                            ...prev.empleado,
                            idSupervisorEntidad: option?.idEntidad || null,
                          },
                        }))
                      }}
                      getOptionLabel={(option) => getEmpleadoNombreCompleto(option)}
                      renderInput={(params) => <TextField {...params} label="Supervisor" fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={negocios}
                      value={selectedNegocio}
                      onChange={(_, option) => {
                        setFormData((prev) => ({
                          ...prev,
                          empleado: {
                            ...prev.empleado,
                            idNegocio: option?.id || null,
                          },
                        }))
                      }}
                      getOptionLabel={(option) => option.descripcion || ""}
                      renderInput={(params) => <TextField {...params} label="Negocio" fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={ubicaciones}
                      value={selectedUbicacion}
                      onChange={(_, option) => {
                        setFormData((prev) => ({
                          ...prev,
                          empleado: {
                            ...prev.empleado,
                            idUbicacionBase: option?.id || null,
                          },
                        }))
                      }}
                      getOptionLabel={(option) => option.descripcion || ""}
                      renderInput={(params) => <TextField {...params} label="Ubicación base" fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="CBU"
                      value={formData.empleado.cbu}
                      onChange={handleEmpleadoChange("cbu")}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Observaciones"
                      value={formData.empleado.observaciones}
                      onChange={handleEmpleadoChange("observaciones")}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {mode === "create" ? (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    Contrato inicial
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Vigencia desde"
                        type="date"
                        value={formData.contrato.fechaVigenciaDesde}
                        onChange={handleContratoChange("fechaVigenciaDesde")}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Vigencia hasta"
                        type="date"
                        value={formData.contrato.fechaVigenciaHasta}
                        onChange={handleContratoChange("fechaVigenciaHasta")}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Puesto"
                        value={formData.contrato.puesto}
                        onChange={handleContratoChange("puesto")}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Autocomplete
                        options={RRHH_TIPOS_JORNADA}
                        value={RRHH_TIPOS_JORNADA.find((option) => option.value === formData.contrato.tipoJornada) || null}
                        onChange={(_, option) => {
                          setFormData((prev) => ({
                            ...prev,
                            contrato: {
                              ...prev.contrato,
                              tipoJornada: option?.value || "",
                            },
                          }))
                        }}
                        getOptionLabel={(option) => option.label}
                        renderInput={(params) => <TextField {...params} label="Tipo jornada" fullWidth />}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Sueldo base"
                        value={formData.contrato.sueldoBase}
                        onChange={handleContratoChange("sueldoBase")}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Pago por hora"
                        value={formData.contrato.pagoPorHora}
                        onChange={handleContratoChange("pagoPorHora")}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Pago por hora extra"
                        value={formData.contrato.pagoPorHoraExtra}
                        onChange={handleContratoChange("pagoPorHoraExtra")}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Sindicato / Gremio"
                        value={formData.contrato.sindicatoGremio}
                        onChange={handleContratoChange("sindicatoGremio")}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Categoría"
                        value={formData.contrato.categoria}
                        onChange={handleContratoChange("categoria")}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Observaciones contrato"
                        value={formData.contrato.observaciones}
                        onChange={handleContratoChange("observaciones")}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Box>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    spacing={1.5}
                    mb={2}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Contrato vigente
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Resumen del último contrato activo del empleado.
                      </Typography>
                    </Box>

                    <Button
                      variant="outlined"
                      startIcon={<AddCardIcon />}
                      onClick={onOpenContrato}
                    >
                      Nuevo contrato
                    </Button>
                  </Stack>

                  {contratoVigente ? (
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} flexWrap="wrap" useFlexGap>
                      <Chip icon={<WorkHistoryIcon />} label={`Desde ${formatFecha(contratoVigente.fechaVigenciaDesde)}`} />
                      {contratoVigente.puesto ? <Chip label={`Puesto: ${contratoVigente.puesto}`} /> : null}
                      {contratoVigente.tipoJornada ? <Chip label={`Jornada: ${contratoVigente.tipoJornada}`} /> : null}
                      {contratoVigente.sueldoBase ? <Chip label={`Sueldo: ${formatCurrency(contratoVigente.sueldoBase)}`} /> : null}
                      {contratoVigente.categoria ? <Chip label={`Categoría: ${contratoVigente.categoria}`} /> : null}
                    </Stack>
                  ) : (
                    <Alert severity="info">El empleado todavía no tiene contratos registrados.</Alert>
                  )}
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={saving || loading}
          >
            {saving ? "Guardando..." : mode === "edit" ? "Guardar cambios" : "Crear empleado"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  )
}

export default ModalRrhhEmpleado
